<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { auth } from '@/firebase/config'
import { useRoute } from 'vue-router'
import GitInfo from './GitInfo.vue'
import { GitInfoService } from '@/services/GitInfoService'
import BaseModal from './BaseModal.vue'
// Obtener información formateada de git
const formattedInfo = computed(() => GitInfoService.getGitSummary())

const props = defineProps({
	isMenuOpen: {
		type: Boolean,
		required: true
	},
	isTestMode: {
		type: Boolean,
		required: true
	}
})

const emit = defineEmits(['update:isMenuOpen', 'logout', 'modeChange', 'update:isTestMode'])

const route = useRoute()
const activeRoute = ref(route.path)
const userData = computed(() => useStore().state.global.user)
const userName = computed(() => userData.value.me?.nombre + ' ' + userData.value.me?.primerApellido + ' ' + userData.value.me?.segundoApellido)
watch(
	() => route.path,
	newPath => {
		activeRoute.value = newPath
	}
)

const userEmail = computed(() => auth.currentUser?.email || 'Usuario')

const closeMenu = () => {
	emit('update:isMenuOpen', false)
}

const showModal = ref(false)
const logout = () => {
	showModal.value = false
	emit('logout')
}

// Función para manejar clics fuera del sidebar
const handleClickOutside = (event: Event) => {
	if (!props.isMenuOpen) return

	const target = event.target as HTMLElement
	const sidebar = document.querySelector('.va-sidebar')
	const navbar = document.querySelector('.va-navbar')

	// Si el clic no fue en el sidebar ni en el navbar, cerrar el sidebar
	if (sidebar && !sidebar.contains(target) && navbar && !navbar.contains(target)) {
		closeMenu()
	}
}

// Función específica para clics en el contenido principal
const handleMainContentClick = () => {
	if (props.isMenuOpen) {
		closeMenu()
	}
}

// Agregar y remover event listener
onMounted(() => {
	document.addEventListener('click', handleClickOutside, true)
})

onUnmounted(() => {
	document.removeEventListener('click', handleClickOutside, true)
})
</script>

<template>
	<div class="main-div">
		<va-sidebar color="background-secondary" class="sidebar-fixed shadow-md border-r border-gray-200" minimizedWidth="110px" v-model="props.isMenuOpen">
			<va-sidebar-item>
				<span style="font-size: 12px; color: #adb5bd; font-weight: 500; margin-left: 4px; letter-spacing: 0.5px">
					Versión: {{ formattedInfo.lastTag !== 'unknown' ? formattedInfo.lastTag : formattedInfo.shortHash }}
				</span>
				<VaDivider />
			</va-sidebar-item>
			<va-sidebar-item :active="activeRoute === '/cuenta'">
				<router-link to="/cuenta" @click="((activeRoute = '/cuenta'), closeMenu())" class="menu-button-link">
					<va-sidebar-item-content class="menu-button">
						<div class="d-flex align-items-center">
							<VaAvatar title="icon face" icon="person" size="small" class="mr-2" />
							<h2>{{ userName.includes('undefined') ? 'Usuario' : userName }}</h2>
						</div>
					</va-sidebar-item-content>
					<va-sidebar-item-title style="padding: 5px">
						<VaBadge :text="userEmail" color="primary" class="ml-2" />
					</va-sidebar-item-title>
					<VaDivider />
				</router-link>
			</va-sidebar-item>
			<VaAccordion>
				<VaCollapse>
					<template #header="{ value: isCollapsed }">
						<VaSidebarItem>
							<VaSidebarItemContent class="menu-button">
								<div class="item-media">
									<div class="icon-circle">
										<i class="fas fa-chart-line"></i>
									</div>
								</div>
								<VaSidebarItemTitle> Bitácora </VaSidebarItemTitle>
								<VaSpacer />
								<VaIcon :name="isCollapsed ? 'va-arrow-up' : 'va-arrow-down'" />
							</VaSidebarItemContent>
						</VaSidebarItem>
					</template>

					<template #body>
						<va-sidebar-item :active="activeRoute === '/admin/bitacora'">
							<router-link to="/admin/bitacora" @click="((activeRoute = '/admin/bitacora'), closeMenu())" class="menu-button-link">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-graduation-cap"></i>
										</div>
									</div>
									<va-sidebar-item-title> Bitácora </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/transacciones'">
							<router-link to="/admin/transacciones" @click="((activeRoute = '/admin/transacciones'), closeMenu())" class="menu-button-link">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-certificate"></i>
										</div>
									</div>
									<va-sidebar-item-title> Transacciones </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/estadisticas'">
							<router-link to="/admin/estadisticas" @click="((activeRoute = '/admin/estadisticas'), closeMenu())" class="menu-button-link">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-university"></i>
										</div>
									</div>
									<va-sidebar-item-title> Estadísticas </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
					</template>
				</VaCollapse>
			</VaAccordion>
			<va-sidebar-item :active="activeRoute === '/admin/registro'">
				<router-link to="/admin/registro" @click="((activeRoute = '/admin/registro'), closeMenu())">
					<va-sidebar-item-content class="menu-button">
						<div class="item-media">
							<div class="icon-circle">
								<i class="fas fa-user-plus"></i>
							</div>
						</div>
						<va-sidebar-item-title> Registro </va-sidebar-item-title>
					</va-sidebar-item-content>
				</router-link>
			</va-sidebar-item>
			<VaAccordion>
				<VaCollapse>
					<template #header="{ value: isCollapsed }">
						<VaSidebarItem>
							<VaSidebarItemContent class="menu-button">
								<div class="item-media">
									<div class="icon-circle">
										<i class="fas fa-chart-line"></i>
									</div>
								</div>
								<VaSidebarItemTitle> Actualizar </VaSidebarItemTitle>
								<VaSpacer />
								<VaIcon :name="isCollapsed ? 'va-arrow-up' : 'va-arrow-down'" />
							</VaSidebarItemContent>
						</VaSidebarItem>
					</template>

					<template #body>
						<va-sidebar-item :active="activeRoute === '/admin/actualizar/plan-estudios'">
							<router-link to="/admin/actualizar/plan-estudios" @click="((activeRoute = '/admin/actualizar/plan-estudios'), closeMenu())" class="menu-button-link">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-graduation-cap"></i>
										</div>
									</div>
									<va-sidebar-item-title> Plan de estudios</va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/actualizar/creditos'">
							<router-link to="/admin/actualizar/creditos" @click="((activeRoute = '/admin/actualizar/creditos'), closeMenu())" class="menu-button-link">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-certificate"></i>
										</div>
									</div>
									<va-sidebar-item-title> Créditos</va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/actualizar/responsables'">
							<router-link to="/admin/actualizar/responsables" @click="((activeRoute = '/admin/actualizar/responsables'), closeMenu())" class="menu-button-link">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-university"></i>
										</div>
									</div>
									<va-sidebar-item-title> Responsables </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/actualizar/configuracion'">
							<router-link to="/admin/actualizar/configuracion" @click="((activeRoute = '/admin/actualizar/configuracion'), closeMenu())" class="menu-button-link">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-university"></i>
										</div>
									</div>
									<va-sidebar-item-title> Configuraciones </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/actualizar/asignaturas'">
							<router-link to="/admin/actualizar/asignaturas" @click="((activeRoute = '/admin/actualizar/asignaturas'), closeMenu())" class="menu-button-link">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-university"></i>
										</div>
									</div>
									<va-sidebar-item-title> Asignaturas </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
					</template>
				</VaCollapse>
			</VaAccordion>
			<va-sidebar-item :active="activeRoute === '/admin/contabilizar'">
				<router-link to="/admin/contabilizar" @click="((activeRoute = '/admin/contabilizar'), closeMenu())">
					<va-sidebar-item-content class="menu-button">
						<div class="item-media">
							<div class="icon-circle">
								<i class="fas fa-calculator"></i>
							</div>
						</div>
						<va-sidebar-item-title> Contabilizar </va-sidebar-item-title>
					</va-sidebar-item-content>
				</router-link>
			</va-sidebar-item>
			<va-sidebar-item :active="activeRoute === '/admin/expiracion'">
				<router-link to="/admin/expiracion" @click="((activeRoute = '/admin/expiracion'), closeMenu())">
					<va-sidebar-item-content class="menu-button">
						<div class="item-media">
							<div class="icon-circle">
								<i class="fas fa-clock"></i>
							</div>
						</div>
						<va-sidebar-item-title> Expiración </va-sidebar-item-title>
					</va-sidebar-item-content>
				</router-link>
			</va-sidebar-item>
			<VaAccordion>
				<VaCollapse>
					<template #header="{ value: isCollapsed }">
						<VaSidebarItem>
							<VaSidebarItemContent class="menu-button">
								<div class="item-media">
									<div class="icon-circle">
										<i class="fas fa-users"></i>
									</div>
								</div>
								<VaSidebarItemTitle> Administración </VaSidebarItemTitle>
								<VaSpacer />
								<VaIcon :name="isCollapsed ? 'va-arrow-up' : 'va-arrow-down'" />
							</VaSidebarItemContent>
						</VaSidebarItem>
					</template>

					<template #body>
						<va-sidebar-item :active="activeRoute === '/admin/institutions'">
							<router-link to="/admin/institutions" @click="((activeRoute = '/admin/institutions'), closeMenu())">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-users"></i>
										</div>
									</div>
									<va-sidebar-item-title> Instituciones </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/mantenimiento'">
							<router-link to="/admin/mantenimiento" @click="((activeRoute = '/admin/mantenimiento'), closeMenu())">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-wrench"></i>
										</div>
									</div>
									<va-sidebar-item-title> Mantenimiento </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/pagos'">
							<router-link to="/admin/pagos" @click="((activeRoute = '/admin/pagos'), closeMenu())">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-money-bill"></i>
										</div>
									</div>
									<va-sidebar-item-title> Pagos </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/facturas'">
							<router-link to="/admin/facturas" @click="((activeRoute = '/admin/facturas'), closeMenu())">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-receipt"></i>
										</div>
									</div>
									<va-sidebar-item-title> Facturas </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
						<va-sidebar-item :active="activeRoute === '/admin/desactivacion'">
							<router-link to="/admin/desactivacion" @click="((activeRoute = '/admin/desactivacion'), closeMenu())">
								<va-sidebar-item-content class="menu-button">
									<div class="item-media">
										<div class="icon-circle">
											<i class="fas fa-ban"></i>
										</div>
									</div>
									<va-sidebar-item-title> Desactivación </va-sidebar-item-title>
								</va-sidebar-item-content>
							</router-link>
						</va-sidebar-item>
					</template>
				</VaCollapse>
			</VaAccordion>
			<va-sidebar-item :active="activeRoute === '/degrees'">
				<router-link to="/degrees" @click="((activeRoute = '/degrees'), closeMenu())">
					<va-sidebar-item-content class="menu-button">
						<div class="item-media">
							<div class="icon-circle">
								<i class="fas fa-university"></i>
							</div>
						</div>
						<va-sidebar-item-title> Certisep </va-sidebar-item-title>
					</va-sidebar-item-content>
				</router-link>
			</va-sidebar-item>
			<va-sidebar-item>
				<va-sidebar-item-content class="menu-button" @click="showModal = true">
					<div class="item-media">
						<div class="icon-circle">
							<i class="fas fa-sign-out-alt"></i>
						</div>
					</div>
					<va-sidebar-item-title><span style="color: red"> CERRAR SESIÓN </span></va-sidebar-item-title>
				</va-sidebar-item-content>
			</va-sidebar-item>
			<GitInfo style="margin-bottom: 100px" />
		</va-sidebar>
		<main class="main-content" :class="{ 'sidebar-open': props.isMenuOpen }" @click="handleMainContentClick">
			<router-view></router-view>
		</main>
	</div>
	<BaseModal :model-value="showModal" @update:model-value="showModal = false" title="Cerrar sesión" message="¿Estás seguro de querer cerrar sesión?" :test-mode="false" size="small">
		<template #title>
			<h2>Cerrar sesión</h2>
		</template>
		<template #body>
			<p style="font-size: 16px; font-weight: 500; color: #333; text-align: center">¿Confirma que desea cerrar sesión?</p>
		</template>
		<template #footer>
			<va-button @click="showModal = false" color="primary" class="mr-2">Cancelar</va-button>
			<va-button @click="logout" color="danger">Cerrar sesión</va-button>
		</template>
	</BaseModal>
</template>

<style scoped>
.main-div {
	height: 100vh;
	width: 100%;
	display: flex;
}
.main-content {
	width: 100%;
	height: calc(100vh - 60px);
	border-width: 0px;
	overflow-y: auto;
	border-color: rgb(255, 255, 255);
	border-style: solid;
	background-color: rgb(255, 255, 255);
	background-position: -1px -1px;
	margin: 0px !important;
	padding: 5px !important;
	transition: margin-left 0.3s ease;
	margin-left: 0 !important; /* Sin margen cuando sidebar está cerrado */
}
.main-content.sidebar-open {
	margin-left: 280px !important; /* Sidebar abierto */
}
/* Solo estilos específicos del componente que no están en global.css */

.menu-button-link {
	text-decoration: none;
	display: block;
	width: 100%;
}

.menu-button {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 8px;
	margin: 8px;
	padding: 12px;
	transition: all 0.3s ease;
	border: 2px solid transparent;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.menu-button:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
	border-color: rgba(255, 255, 255, 0.3);
}

.menu-button:active {
	transform: translateY(0);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.welcome-section h2 {
	margin: 0;
	font-size: 1.2rem;
	color: #333;
}

.item-media {
	display: flex;
	align-items: center;
	margin-right: 1rem;
}

.icon-circle {
	width: 40px;
	height: 40px;
	background-color: #062342;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.icon-circle i {
	color: white;
	font-size: 1.2rem;
}

.menu-item span {
	font-weight: 500;
	text-transform: uppercase;
	font-size: 0.9rem;
}

/* Estilos para el scroll personalizado */
.main-content::-webkit-scrollbar {
	width: 8px;
}

.main-content::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.main-content::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.main-content::-webkit-scrollbar-thumb:hover {
	background: #555;
}

.sidebar-fixed {
	position: fixed;
	top: 85px;
	left: 0;
	height: calc(100vh - 60px);
	z-index: 999;
	transition: width 0.3s ease;
	width: 110px; /* Ancho por defecto */
	margin-top: 15px; /* Espacio para el navbar fijo */
}

/* Responsive para sidebar minimizado */
@media (max-width: 768px) {
	.main-content {
		margin-left: 0 !important;
	}
	.main-content.sidebar-open {
		margin-left: 280px !important;
	}

	.sidebar-fixed {
		width: 110px;
	}
}

/* Estilos para pantallas más grandes */
@media (min-width: 769px) {
	.main-content {
		margin-left: 0 !important;
	}
	.main-content.sidebar-open {
		margin-left: 280px !important;
	}

	.sidebar-fixed {
		width: 110px;
	}
}
</style>
